import { ReactNode, useMemo } from 'react';

import { Card, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import {
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import { COMPLIANCE_COLORS } from '../utils/charts';
import { complianceLevel, incidentPctIsHigh } from '../utils/compliance';
import {
  formatDecimal,
  formatInt,
  joinParts,
  zoneLabel,
} from '../utils/format';
import { dayHeaderLabel, vanColorMap } from '../utils/mapData';
import { VisitSelection, aggregateStops } from '../utils/selection';
import {
  METRIC_COLUMNS,
  MetricKey,
  NAME_COLUMN_WIDTH,
  ZONES_COLUMN_WIDTH,
  columnIndexOf,
} from '../utils/vansMetricColumns';
import {
  VansMetrics,
  VansTableRow,
  buildTotalRow,
  buildVanRows,
  rowMatchesSelection,
} from '../utils/vansTable';

const I18N_BASE = 'scenes.visit_history.vans_table';
const CSS = 'visit-history-vans';
const SCROLL_X =
  NAME_COLUMN_WIDTH +
  ZONES_COLUMN_WIDTH +
  METRIC_COLUMNS.reduce((sum, column) => sum + column.width, 0);
const EMPTY = '—';

interface VisitVansTableProps {
  summary: TiendaPerfectaVisitHistorySummary;
  /** The flat stops: every figure of the table is derived from them. */
  stops: TiendaPerfectaVisitHistoryStop[];
  selection: VisitSelection;
  /** Toggling setter of the shared selection. */
  onSelect: (selection: NonNullable<VisitSelection>) => void;
  timeZone: string;
}

/**
 * Text color of a metric cell. Fixed semantic constants (never
 * `colorPrimary`): executed turquoise, incident red, material blue, pending
 * grey (lighter at zero) and the incident share red only above 10 %.
 */
const metricClass = (key: MetricKey, m: CellMetrics): string => {
  switch (key) {
    case 'executed':
      return `${CSS}__cell--exec`;
    case 'incidents':
      return `${CSS}__cell--inc`;
    case 'incidentPct':
      return incidentPctIsHigh(m.incidentPct)
        ? `${CSS}__cell--inc`
        : `${CSS}__cell--muted`;
    case 'pending':
      return m.pending > 0 ? `${CSS}__cell--pend` : `${CSS}__cell--zero`;
    case 'material':
      return `${CSS}__cell--mat`;
    default:
      return '';
  }
};

/** Metrics of a row or of the total: the counts every column reads. */
type CellMetrics = VansMetrics & { routes: number; days: number };

const numeric = (value: number | null): number => value ?? -1;

function VisitVansTable({
  summary,
  stops,
  selection,
  onSelect,
  timeZone,
}: VisitVansTableProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const aggregated = useMemo(
    () => aggregateStops(stops, timeZone),
    [stops, timeZone]
  );
  const rows = useMemo(
    () => buildVanRows(summary, aggregated, vanColorMap(stops), timeZone),
    [summary, aggregated, stops, timeZone]
  );
  const total = useMemo(() => buildTotalRow(aggregated), [aggregated]);

  const zoneName = (zone: { name: string }) =>
    zoneLabel(zone, t('scenes.visit_history.zone_unnamed'));
  const int = (n: number) => formatInt(n, lang);
  const dec = (n: number | null) =>
    n === null ? EMPTY : formatDecimal(n, lang);

  const renderMetric = (key: MetricKey, m: CellMetrics): ReactNode => {
    switch (key) {
      case 'planned':
        return int(m.planned);
      case 'executed':
        return int(m.executed);
      case 'incidents':
        return int(m.incidents);
      case 'incidentPct':
        return `${formatDecimal(m.incidentPct, lang)}%`;
      case 'pending':
        return int(m.pending);
      case 'compliance':
        return (
          <span className={`${CSS}__cum`}>
            <span className={`${CSS}__track`}>
              <span
                className={`${CSS}__fill`}
                style={{
                  width: `${Math.min(100, m.compliancePct)}%`,
                  background:
                    COMPLIANCE_COLORS[complianceLevel(m.compliancePct)],
                }}
              />
            </span>
            <b className={`${CSS}__pct is-${complianceLevel(m.compliancePct)}`}>
              {formatDecimal(m.compliancePct, lang)}%
            </b>
          </span>
        );
      case 'material':
        return int(m.materialUnits);
      case 'routes':
        return int(m.routes);
      case 'days':
        return int(m.days);
      case 'avgStart':
        return m.avgStart ?? EMPTY;
      case 'avgEnd':
        return m.avgEnd ?? EMPTY;
      case 'duration':
        return dec(m.avgDurationHours);
      case 'vph':
        return dec(m.visitsPerHour);
    }
  };

  const sortValue = (key: MetricKey, m: CellMetrics): number => {
    switch (key) {
      case 'planned':
        return m.planned;
      case 'executed':
        return m.executed;
      case 'incidents':
        return m.incidents;
      case 'incidentPct':
        return m.incidentPct;
      case 'pending':
        return m.pending;
      case 'compliance':
        return m.compliancePct;
      case 'material':
        return m.materialUnits;
      case 'routes':
        return m.routes;
      case 'days':
        return m.days;
      // Sorted as minutes since midnight (not as text); a missing value is
      // -1, so it sorts before every real time.
      case 'avgStart':
        return numeric(toMinutes(m.avgStart));
      case 'avgEnd':
        return numeric(toMinutes(m.avgEnd));
      case 'duration':
        return numeric(m.avgDurationHours);
      case 'vph':
        return numeric(m.visitsPerHour);
    }
  };

  const metricColumn = (
    key: MetricKey,
    titleKey: string,
    width: number
  ): ColumnsType<VansTableRow>[number] => ({
    key,
    title: t(`${I18N_BASE}.columns.${titleKey}`),
    width,
    align: 'right',
    sorter: (a, b) => sortValue(key, a) - sortValue(key, b),
    defaultSortOrder: key === 'compliance' ? 'ascend' : undefined,
    onCell: (row) => ({ className: metricClass(key, row) }),
    render: (_, row) => <span>{renderMetric(key, row)}</span>,
  });

  const columns: ColumnsType<VansTableRow> = [
    {
      key: 'name',
      title: t(`${I18N_BASE}.columns.van`),
      width: NAME_COLUMN_WIDTH,
      fixed: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name, lang),
      render: (_, row) =>
        row.kind === 'van' ? (
          <span>
            <i className={`${CSS}__dot`} style={{ background: row.color }} />
            <span className={`${CSS}__name`}>{row.name}</span>
          </span>
        ) : (
          <span className={`${CSS}__name ${CSS}__name--child`}>
            {joinParts([dayHeaderLabel(row.date, lang), row.name])}
          </span>
        ),
    },
    {
      key: 'zones',
      title: t(`${I18N_BASE}.columns.zones`),
      width: ZONES_COLUMN_WIDTH,
      sorter: (a, b) =>
        a.zones
          .map((z) => z.name)
          .join()
          .localeCompare(b.zones.map((z) => z.name).join(), lang),
      render: (_, row) =>
        row.kind === 'van' ? (
          <span className={`${CSS}__zones`}>
            {row.zones.map((zone) => (
              <button
                key={zone.id}
                type="button"
                className={`${CSS}__ztag`}
                aria-label={t(`${I18N_BASE}.select_zone`, {
                  name: zoneName(zone),
                })}
                onClick={(event) => {
                  // The row click selects the van; the tag selects the zone.
                  event.stopPropagation();
                  onSelect({ kind: 'zone', zoneId: zone.id });
                }}
              >
                {zoneName(zone)}
              </button>
            ))}
          </span>
        ) : (
          <span>{row.zones.map(zoneName).join(', ')}</span>
        ),
    },
    ...METRIC_COLUMNS.map(({ key, titleKey, width }) =>
      metricColumn(key, titleKey, width)
    ),
  ];

  const summaryRow = (
    <Table.Summary.Row className={`${CSS}__total`}>
      <Table.Summary.Cell index={0}>
        <b>{t(`${I18N_BASE}.total.label`)}</b>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        {t(`${I18N_BASE}.total.zones`, { count: total.zonesCount })}
      </Table.Summary.Cell>
      {METRIC_COLUMNS.map(({ key }) => (
        <Table.Summary.Cell
          key={key}
          index={columnIndexOf(
            columns.map((column) => String(column.key)),
            key
          )}
          align="right"
          className={metricClass(key, total)}
        >
          {renderMetric(key, total)}
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  const isSelected = (row: VansTableRow): boolean => {
    if (!selection) return false;
    if (selection.kind === 'van') {
      return row.kind === 'van' && row.vanId === selection.vanId;
    }

    return (
      selection.kind === 'route' &&
      row.kind === 'execution' &&
      row.routeId === selection.routeId &&
      row.date === selection.date
    );
  };

  const legendItem = (level: 'bad' | 'warn' | 'ok') => (
    <span key={level}>
      <i style={{ background: COMPLIANCE_COLORS[level] }} />
      {t(`${I18N_BASE}.legend.${level}`)}
    </span>
  );

  return (
    <Card
      className={CSS}
      title={t(`${I18N_BASE}.title`)}
      extra={
        <div className={`${CSS}__legend`}>
          <span>{t(`${I18N_BASE}.legend.label`)}</span>
          {(['bad', 'warn', 'ok'] as const).map(legendItem)}
        </div>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Table<VansTableRow>
        columns={columns}
        dataSource={rows}
        rowKey="key"
        pagination={false}
        scroll={{ x: SCROLL_X }}
        expandable={{ indentSize: 20 }}
        rowClassName={(row) =>
          [
            `${CSS}__row`,
            isSelected(row) ? 'is-selected' : '',
            rowMatchesSelection(row, selection) ? '' : 'is-dimmed',
            row.kind === 'execution' ? `${CSS}__row--child` : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        onRow={(row) => ({
          onClick: () =>
            onSelect(
              row.kind === 'van'
                ? { kind: 'van', vanId: row.vanId }
                : { kind: 'route', routeId: row.routeId, date: row.date }
            ),
        })}
        summary={() => <Table.Summary>{summaryRow}</Table.Summary>}
      />
      <div className={`${CSS}__note`}>{t(`${I18N_BASE}.note`)}</div>
    </Card>
  );
}

const toMinutes = (clock: string | null): number | null => {
  if (!clock) return null;
  const [h, m] = clock.split(':').map(Number);

  return h * 60 + m;
};

export default VisitVansTable;
